import React, { useState, useEffect } from 'react';
import { Skeleton } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { send_request } from '../../../utils/Request';
import TaskAssigneeAvatars from '../../Task/TaskAssigneeAvatars';
import './TaskSummaryCardV2.css';

dayjs.extend(utc);
dayjs.extend(timezone);

function TaskSummaryCardV2() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentTasks();
  }, []);

  const fetchRecentTasks = async () => {
    setLoading(true);
    try {
      const queryBody = {
        queryString: null,
        workflowId: null,
        statusToInclude: ['TODO'],
        lastTask: null,
      };

      const response = await send_request(
        'project-service/project/getTasks/',
        queryBody,
        null,
        'POST'
      );

      if (response?.data?.data) {
        const allTasks = response.data.data.slice(0, 5); // Limit to 5 tasks
        const now = new Date();

        const processedTasks = allTasks.map(task => {
          let isOverdue = false;
          if (task.dueDate) {
            const dueDate = new Date(task.dueDate);
            isOverdue = now > dueDate;
          }
          return { ...task, isOverdue };
        });

        setTasks(processedTasks);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const openTask = taskId => {
    window.open(`/task?status=TODO&openPanel=${taskId}`, '_blank');
  };

  const stripHtml = html => {
    if (!html) return '';
    return html.replace(/(<([^>]+)>)/gi, '').trim();
  };

  const isToday = date => {
    const today = dayjs().tz().startOf('day');
    return dayjs(date).tz().isSame(today, 'day');
  };

  const isYesterday = date => {
    const yesterday = dayjs().tz().subtract(1, 'day').startOf('day');
    return dayjs(date).tz().isSame(yesterday, 'day');
  };

  const formatDateHeader = date => {
    if (!date) return 'Unknown';
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return dayjs(date).tz().format('dddd, D MMMM YYYY');
  };

  const groupTasksByDate = tasks => {
    const groups = {};

    tasks.forEach(task => {
      const date = task.assignedOn
        ? dayjs(task.assignedOn).tz().startOf('day').format('YYYY-MM-DD')
        : 'unknown';

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(task);
    });

    return Object.keys(groups)
      .sort((a, b) => b.localeCompare(a)) // Most recent first
      .map(date => ({
        date: date === 'unknown' ? null : date,
        tasks: groups[date],
      }));
  };

  const renderLoadingSkeleton = () => (
    <div className="task-list">
      {[1, 2, 3, 4, 5].map(i => (
        <div className="task-skeleton-item" key={i}>
          <Skeleton variant="rounded" width={80} height={28} />
          <div className="task-skeleton-content">
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="40%" height={18} />
          </div>
          <Skeleton variant="circular" width={28} height={28} />
        </div>
      ))}
    </div>
  );

  const renderEmptyState = () => (
    <div className="task-empty-state">
      <AccessTimeIcon />
      <p className="task-empty-state-title">No pending tasks</p>
      <p className="task-empty-state-subtitle">You're all caught up!</p>
    </div>
  );

  const renderTaskItem = task => (
    <div className="task-item" key={task.taskId} onClick={() => openTask(task.taskId)}>
      <div className={`task-status-badge ${task.isOverdue ? 'overdue' : 'todo'}`}>
        {task.isOverdue ? (
          <>
            <WarningAmberIcon />
            Overdue
          </>
        ) : (
          <>
            <AccessTimeIcon />
            To do
          </>
        )}
      </div>

      <div className="task-content">
        <p className="task-subject">{task.taskSubject || 'Untitled task'}</p>
        <p className="task-description">{stripHtml(task.taskDescription) || 'No description'}</p>
      </div>

      <div className="task-assignees">
        <TaskAssigneeAvatars assignees={task.assignedTo} max={3} unassignedText="" />
      </div>
    </div>
  );

  const renderTasks = () => {
    const groupedTasks = groupTasksByDate(tasks);

    return (
      <div className="task-list">
        {groupedTasks.map(({ date, tasks: groupTasks }) => (
          <React.Fragment key={date || 'unknown'}>
            <div className="task-date-header">{formatDateHeader(date)}</div>
            {groupTasks.map(task => renderTaskItem(task))}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="task-card-container">
      <div className="task-card-header">
        <h3 className="task-card-title">Recent Tasks</h3>
        <a
          className="task-card-view-all"
          href="/task?status=TODO"
          target="_blank"
          rel="noopener noreferrer"
        >
          View all
          <ArrowForwardIcon />
        </a>
      </div>

      {loading ? renderLoadingSkeleton() : tasks.length > 0 ? renderTasks() : renderEmptyState()}
    </div>
  );
}

export default TaskSummaryCardV2;
